/**
 * Color Mode Helper - Centralized logic for handling wave coloring
 * Eliminates duplication across visualizers
 */

import { Wave } from '../../../types';
import { ColorMode } from './types';

export interface IColorProvider {
  /**
   * Get the color for a wave based on color mode
   */
  getWaveColor(wave: Wave, colorMode: ColorMode): string;
  
  /**
   * Get channel-based color
   */
  getChannelColor(channel?: string): string;
  
  /**
   * Get wave's own color
   */
  getWaveOwnColor(wave: Wave): string;
  
  /**
   * Get color with alpha applied
   */
  getColorWithAlpha(color: string, alpha: number): string;
}

export class ColorModeHelper implements IColorProvider {
  /**
   * Main entry point - gets wave color based on mode
   */
  getWaveColor(wave: Wave, colorMode: ColorMode = 'wave'): string {
    return colorMode === 'channel' 
      ? this.getChannelColor(wave.channel)
      : this.getWaveOwnColor(wave);
  }

  /**
   * Map channel to color: Left=Blue, Right=Pink, Both/Default=Green
   */
  getChannelColor(channel?: string): string {
    if (channel === 'left') return '#3b82f6';
    if (channel === 'right') return '#ec4899';
    return '#10B981';
  }

  /**
   * Get wave's configured color
   */
  getWaveOwnColor(wave: Wave): string {
    return wave.color || '#10B981';
  }

  /**
   * Parse hex color and apply alpha
   */
  getColorWithAlpha(color: string, alpha: number): string {
    try {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } catch {
      return `rgba(100, 100, 100, ${alpha})`;
    }
  }

  /**
   * Interpolate between two colors based on a normalized value (0-1)
   */
  interpolateColor(color1: string, color2: string, t: number): string {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);

    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);

    const r = Math.floor(r1 + (r2 - r1) * t);
    const g = Math.floor(g1 + (g2 - g1) * t);
    const b = Math.floor(b1 + (b2 - b1) * t);

    return `rgb(${r}, ${g}, ${b})`;
  }
}

// Singleton instance for use throughout the app
export const colorHelper = new ColorModeHelper();
